import classnames from 'classnames';
import { addDays, getDate, startOfDay } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ReactResizeDetector from 'react-resize-detector';

import { useAuth } from '../../app/AuthorizationProvider';
import { CustomXAxis } from '../../scenes/Scheduling/SchedulingComposer/GanttChart/CustomXAxis';
import { CHART_WIDTH, HOURS_24 } from '../../scenes/Scheduling/config';
import { transformSourceScheduleElements } from '../../scenes/Scheduling/utils/transformScheduleElementsData';
import { getScrollPosition } from '../../utils/chart';
import { UTC_TIMEZONE_NAME, getTimeZone, getZonedDateTimezoneOffset } from '../../utils/date';
import { FullSizeMessage } from '../FullSizeMessage';
import { GanttChart } from './GanttChart';
import { GanttYLabelContainer } from './GanttYLabelContainer';
import { OverviewInformation } from './OverviewInformation';
import { transformScheduleElementsData } from './transformData';
import { MyScheduleWeeklyDayProps, MyScheduleWeeklyProps, ScheduleElementType } from './types';
import { findTimezoneDSTChangeWithinRange } from './utils';

export const MyScheduleWeeklyComponent: React.FC<MyScheduleWeeklyProps> = ({
  highlightedDate,
  overviewStats,
  selectedDate,
  scheduleItems,
  dailyShifts,
  scheduleElements: scheduleElementsData,
  t,
}) => {
  const scrollbarRef = useRef<PerfectScrollbar>(null);
  const chartRef = useRef(null);
  const [scheduleElements, setScheduleElements] = useState<ScheduleElementType[]>([]);
  const [daysRelatedData, setDaysRelatedData] = useState<MyScheduleWeeklyDayProps[]>([]);
  let scrollbarContainer: HTMLElement | undefined;

  const { timezone, localeObj } = useAuth();

  const endDate = addDays(selectedDate, 6);

  useLayoutEffect(() => {
    if (scheduleElementsData.length && scheduleItems.length) {
      const transformedScheduleElementsData = transformSourceScheduleElements(scheduleElementsData, t);

      const { days, elements } = transformScheduleElementsData(
        scheduleItems,
        transformedScheduleElementsData,
        dailyShifts,
        selectedDate,
        endDate,
        timezone,
        localeObj
      );
      setScheduleElements(elements);
      setDaysRelatedData(days);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleItems, scheduleElementsData]);

  useEffect(() => {
    if (scrollbarContainer) {
      const { x } = getScrollPosition(
        scheduleElements,
        getZonedDateTimezoneOffset(zonedTimeToUtc(selectedDate, getTimeZone()), UTC_TIMEZONE_NAME) * 60 * 1000
      );
      scrollbarContainer.scrollLeft = x;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleElements, selectedDate]);

  const assignScrollbarContainer = (container: HTMLElement) => {
    scrollbarContainer = container;
  };

  const currentDay = startOfDay(new Date());
  const hasEndDateTwoNumbersLength = getDate(endDate) > 9;

  const onResizeHandler = () => {
    scrollbarRef?.current?.forceUpdate();
  };

  const timezoneDSTChange = findTimezoneDSTChangeWithinRange(selectedDate, endDate, timezone);

  return scheduleElements.length ? (
    <>
      <OverviewInformation {...overviewStats} t={t} testIdPrefix="my-schedule:weekly-view" />
      <div data-test-id="chart-wrapper" className="d-flex mbxl align-items-end">
        <GanttYLabelContainer
          currentDay={currentDay}
          highlightedDate={highlightedDate}
          data={daysRelatedData}
          t={t}
          timezone={timezone}
          dayClassName={classnames({ 'wider-container': hasEndDateTwoNumbersLength })}
        />
        <ReactResizeDetector handleWidth onResize={onResizeHandler}>
          <PerfectScrollbar className="gantt-wrapper" ref={scrollbarRef} containerRef={assignScrollbarContainer}>
            <CustomXAxis
              start={0}
              highlightDSTChange={timezoneDSTChange}
              end={HOURS_24}
              width={CHART_WIDTH}
              timezone={UTC_TIMEZONE_NAME}
              testIdPrefix="my-schedule:weekly-view"
            />
            <GanttChart chartRef={chartRef} elements={scheduleElements} t={t} timezone={timezone} />
          </PerfectScrollbar>
        </ReactResizeDetector>
      </div>
    </>
  ) : (
    <FullSizeMessage iconKind="conversation-video" headline={t('schedule.mySchedule.noSchedulePresent')} />
  );
};

export const MyScheduleWeekly = withTranslation()(MyScheduleWeeklyComponent);
